import { useEffect, useRef, useState } from "react";
import {
  Card,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Stack,
  Chip,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import Loader from "examples/Loader";
import CustomDataTable from "../Users/CustomDataTable";
import { useAppServices } from "hook/services";

const formatAUD = (n) =>
  `A$${Number(n || 0).toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "-");

const statusChip = (paymentStatus) => {
  const styles = {
    Pending: { bg: "#FEF3C7", fg: "#92400E" },
    Success: { bg: "#D1FAE5", fg: "#065F46" },
  };
  const s = styles[paymentStatus] || { bg: "#E5E7EB", fg: "#374151" };
  return (
    <span
      style={{
        background: s.bg,
        color: s.fg,
        padding: "4px 10px",
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {paymentStatus}
    </span>
  );
};

function BookingLedger() {
  const AppService = useAppServices();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({
    pending: { count: 0, total: 0, commission: 0 },
    success: { count: 0, total: 0, commission: 0 },
  });
  const [loader, setLoader] = useState(true);

  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const typingRef = useRef(null);

  const fetchLedger = async (overrides = {}) => {
    setLoader(true);
    try {
      const params = {
        page: overrides.page ?? pagination.page,
        limit: overrides.limit ?? pagination.limit,
        status: overrides.status ?? status,
        search: overrides.search ?? search,
        startDate: overrides.startDate ?? startDate ?? "",
        endDate: overrides.endDate ?? endDate ?? "",
      };
      const query = Object.entries(params)
        .filter(([, v]) => v !== "" && v !== null && v !== undefined)
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join("&");

      console.log("════════════════════════════════════════════════════");
      console.log("[BookingLedger] 🔵 FETCH → query:", query);
      const { response } = await AppService.booking.ledger({ query });
      console.log("[BookingLedger] 📥 RESPONSE:", response);
      console.log("[BookingLedger] data rows:", response?.data?.length || 0);
      console.log("[BookingLedger] summary:", response?.summary);
      console.log("[BookingLedger] pagination:", response?.pagination);
      console.log("════════════════════════════════════════════════════");

      if (response?.success) {
        setRows(response.data || []);
        setSummary(
          response.summary || {
            pending: { count: 0, total: 0, commission: 0 },
            success: { count: 0, total: 0, commission: 0 },
          }
        );
        setPagination(response.pagination || pagination);
      } else {
        console.warn("[BookingLedger] ⚠️  response.success was false:", response);
        setRows([]);
      }
    } catch (err) {
      console.error("[BookingLedger] ❌ Ledger fetch error:", err);
      setRows([]);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchLedger({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, startDate, endDate]);

  useEffect(() => {
    if (typingRef.current) clearTimeout(typingRef.current);
    typingRef.current = setTimeout(() => {
      fetchLedger({ page: 1, search });
    }, 600);
    return () => clearTimeout(typingRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handlePageChange = (newPage) => fetchLedger({ page: newPage });
  const handleLimitChange = (newLimit) =>
    fetchLedger({ page: 1, limit: newLimit });

  const dataTableData = {
    columns: [
      { Header: "Booking No", accessor: "bookingNo" },
      { Header: "Date", accessor: "createdAt" },
      { Header: "Customer", accessor: "customer" },
      { Header: "Listing", accessor: "listing" },
      { Header: "Shoot Date", accessor: "shootDate" },
      { Header: "Base Price", accessor: "basePrice" },
      { Header: "Total", accessor: "total" },
      { Header: "Aloca Commission (25%)", accessor: "alocaCommission" },
      { Header: "Host Share (75%)", accessor: "hostShare" },
      { Header: "Payment", accessor: "paymentStatus" },
      { Header: "Status", accessor: "status" },
    ],
    rows: rows.map((r) => ({
      bookingNo: r.bookingNo || "-",
      createdAt: formatDate(r.createdAt),
      customer: r.customer,
      listing: r.listing,
      shootDate: formatDate(r.shootDate),
      basePrice: formatAUD(r.basePrice),
      total: formatAUD(r.total),
      alocaCommission: (
        <MDTypography variant="caption" fontWeight="bold" color="info">
          {formatAUD(r.alocaCommission)}
        </MDTypography>
      ),
      hostShare: formatAUD(r.hostShare),
      paymentStatus: statusChip(r.paymentStatus),
      status: r.status || "-",
    })),
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Statistics cards */}
        <Grid container spacing={3} mb={2}>
          <Grid item xs={12} md={6} lg={3}>
            <ComplexStatisticsCard
              color="warning"
              icon="hourglass_top"
              title="Payment (Pending)"
              count={summary.pending.count}
              percentage={{
                color: "warning",
                amount: formatAUD(summary.pending.total),
                label: "total pending value",
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <ComplexStatisticsCard
              color="info"
              icon="account_balance_wallet"
              title="Payment (Pending) Aloca "
              count={formatAUD(summary.pending.commission)}
              percentage={{
                color: "info",
                amount: "25%",
                label: "of base price",
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <ComplexStatisticsCard
              color="success"
              icon="check_circle"
              title="Success Bookings"
              count={summary.success.count}
              percentage={{
                color: "success",
                amount: formatAUD(summary.success.total),
                label: "total received",
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <ComplexStatisticsCard
              color="primary"
              icon="paid"
              title="Earned Aloca Commission"
              count={formatAUD(summary.success.commission)}
              percentage={{
                color: "success",
                amount: "25%",
                label: "from paid bookings",
              }}
            />
          </Grid>
        </Grid>

        <Card>
          <MDBox
            mx={2}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6" color="white">
              Payment Management
            </MDTypography>
            <Stack direction="row" spacing={1}>
              <Chip
                label={`Pending: ${summary.pending.count}`}
                sx={{ background: "#FEF3C7", color: "#92400E", fontWeight: 600 }}
              />
              <Chip
                label={`Success: ${summary.success.count}`}
                sx={{ background: "#D1FAE5", color: "#065F46", fontWeight: 600 }}
              />
            </Stack>
          </MDBox>

          {/* Filters */}
          <MDBox pt={3} px={3}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={status}
                    label="Status"
                    onChange={(e) => setStatus(e.target.value)}
                    sx={{ height: 40 }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="success">Success</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Search Booking No"
                  size="small"
                  fullWidth
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  label="Start Date"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  label="End Date"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <button
                  type="button"
                  onClick={() => {
                    setStatus("all");
                    setSearch("");
                    setStartDate("");
                    setEndDate("");
                  }}
                  style={{
                    background: "#374151",
                    color: "white",
                    padding: "8px 14px",
                    borderRadius: 6,
                    border: "none",
                    cursor: "pointer",
                    width: "100%",
                  }}
                >
                  Reset Filters
                </button>
              </Grid>
            </Grid>

          </MDBox>

          {/* Table */}
          <MDBox pt={2} px={3} pb={3}>
            {loader ? (
              <Loader />
            ) : (
              <CustomDataTable
                table={dataTableData}
                canSearch={false}
                entriesPerPage={{
                  defaultValue: pagination.limit || 10,
                  entries: [5, 10, 15, 20, 25, 50],
                }}
                pagination={pagination}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
              />
            )}
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default BookingLedger;
